/**
 * Low-level analytics dispatch hook for Direct Sharing feature.
 *
 * Provides type-safe track functions for each sharing event.
 * Higher-level hooks (useShareModalTracking, useShareSearchTracking, etc.)
 * compose these into semantic callbacks for components.
 */
import { track } from '@/lib/analytics/track';
import { type TrackingEventV2, TrackingEventName } from '@/models/analytics/eventTypes';
import { useCallback, useMemo } from 'react';

/**
 * Factory: creates a stable, memoized track function for a given event name.
 *
 * `blockAmplitude` opts the event out of Amplitude (Snowplow + Datadog still
 * fire). New events MUST set this per the Snowplow-first routing policy —
 * Amplitude is ON by default in the v2 pipeline. Existing sharing events are
 * grandfathered and keep their Amplitude stream.
 */
function useTrackEvent<E extends TrackingEventV2['event_name']>(
  eventName: E,
  blockAmplitude = false
) {
  return useCallback(
    (props: Extract<TrackingEventV2, { event_name: E }>['props']) => {
      track({
        event_name: eventName,
        props,
        options: blockAmplitude ? { posthog: true, amplitude: false } : { posthog: true },
      } as TrackingEventV2 & {
        options: { posthog: boolean; amplitude?: boolean };
      });
    },
    [eventName, blockAmplitude]
  );
}

export function useSharingAnalytics() {
  const trackShareButtonClicked = useTrackEvent(TrackingEventName.ShareButtonClicked);
  const trackShareModalOpened = useTrackEvent(TrackingEventName.ShareModalOpened);
  const trackShareMemberSearched = useTrackEvent(TrackingEventName.ShareMemberSearched);
  const trackShareMemberAdded = useTrackEvent(TrackingEventName.ShareMemberAdded);
  const trackShareLinkGenerated = useTrackEvent(TrackingEventName.ShareLinkGenerated);
  const trackShareLinkCopied = useTrackEvent(TrackingEventName.ShareLinkCopied);
  const trackShareSaved = useTrackEvent(TrackingEventName.ShareSaved);
  const trackShareAccessChanged = useTrackEvent(TrackingEventName.ShareAccessChanged);
  const trackShareAccessRevoked = useTrackEvent(TrackingEventName.ShareAccessRevoked);
  const trackShareEditRequested = useTrackEvent(TrackingEventName.ShareEditRequested);
  const trackShareNotificationReceived = useTrackEvent(
    TrackingEventName.ShareNotificationReceived
  );
  const trackShareNotificationClicked = useTrackEvent(
    TrackingEventName.ShareNotificationClicked
  );
  const trackShareExternalConfirmed = useTrackEvent(
    TrackingEventName.ShareExternalConfirmed
  );
  const trackShareExternalEmailAdded = useTrackEvent(
    TrackingEventName.ShareExternalEmailAdded
  );
  const trackShareExternalEmailRemoved = useTrackEvent(
    TrackingEventName.ShareExternalEmailRemoved
  );
  const trackShareExternalInlineOpened = useTrackEvent(
    TrackingEventName.ShareExternalInlineOpened
  );
  const trackShareExternalInlineCancelled = useTrackEvent(
    TrackingEventName.ShareExternalInlineCancelled
  );
  // New events — Snowplow-first: opt out of Amplitude (the dangerous default).
  const trackShareExternalRevealOpened = useTrackEvent(
    TrackingEventName.ShareExternalRevealOpened,
    true
  );
  const trackShareExternalLinkSendSubmitted = useTrackEvent(
    TrackingEventName.ShareExternalLinkSendSubmitted,
    true
  );
  const trackSessionLinkShareOtpVerified = useTrackEvent(
    TrackingEventName.SessionLinkShareOtpVerified
  );
  const trackSessionLinkShareOtpFailed = useTrackEvent(
    TrackingEventName.SessionLinkShareOtpFailed
  );

  return useMemo(
    () => ({
      trackShareButtonClicked,
      trackShareModalOpened,
      trackShareMemberSearched,
      trackShareMemberAdded,
      trackShareLinkGenerated,
      trackShareLinkCopied,
      trackShareSaved,
      trackShareAccessChanged,
      trackShareAccessRevoked,
      trackShareEditRequested,
      trackShareNotificationReceived,
      trackShareNotificationClicked,
      trackShareExternalConfirmed,
      trackShareExternalEmailAdded,
      trackShareExternalEmailRemoved,
      trackShareExternalInlineOpened,
      trackShareExternalInlineCancelled,
      trackShareExternalRevealOpened,
      trackShareExternalLinkSendSubmitted,
      trackSessionLinkShareOtpVerified,
      trackSessionLinkShareOtpFailed,
    }),
    [
      trackShareButtonClicked,
      trackShareModalOpened,
      trackShareMemberSearched,
      trackShareMemberAdded,
      trackShareLinkGenerated,
      trackShareLinkCopied,
      trackShareSaved,
      trackShareAccessChanged,
      trackShareAccessRevoked,
      trackShareEditRequested,
      trackShareNotificationReceived,
      trackShareNotificationClicked,
      trackShareExternalConfirmed,
      trackShareExternalEmailAdded,
      trackShareExternalEmailRemoved,
      trackShareExternalInlineOpened,
      trackShareExternalInlineCancelled,
      trackShareExternalRevealOpened,
      trackShareExternalLinkSendSubmitted,
      trackSessionLinkShareOtpVerified,
      trackSessionLinkShareOtpFailed,
    ]
  );
}
